import {
  searchAndFiltersFilterClick,
  searchAndFiltersFinished,
  searchAndFiltersNoResultsTryToClick,
  searchAndFiltersShowAllClick,
  searchAndFiltersStarted,
} from '@wix/bi-logger-media-data/v2';
import {
  searchAndFiltersFilterClickParams,
  searchAndFiltersFinishedParams,
  searchAndFiltersShowAllClickParams,
  searchAndFiltersStartedParams,
} from '@wix/bi-logger-media-data/v2/types';
import { useCallback, useMemo } from 'react';
import { BIPageName } from '../../../../constants/Bi';
import { useSearchResultsBI } from '../../../../containers/FilesManager/SearchResults/useSearchResultsBI';
import { bi } from '../../../../services/loggers/bi';
import { now } from '../../../env/performance';
import { ClickOriginsSearchAndFiltersClear, ErrorTypes } from './useTrackSearchAndFiltersAnalytics.types';

let currentRequest: searchAndFiltersStartedParams = null;
let requestStartTime: number = null;

export const useTrackSearchAndFiltersAnalytics = () => {
  const trackSearchAndFiltersFinished = useCallback((params: searchAndFiltersFinishedParams = {}) => {
    if (!currentRequest) {
      return;
    }

    let currentCategory: string;
    try {
      // if search is done because user clicked category filter
      // filterCategory will be stringified object with {id,localized title}
      currentCategory = JSON.parse(currentRequest.filterCategory).id;
    } catch {
      // otherwise it is a string
      currentCategory = currentRequest.filterCategory;
    }

    const { errorType = null, numOfResults, numOfResultsOtherCategories = null } = params;

    const updatedParams = {} as searchAndFiltersFinishedParams;
    if (numOfResultsOtherCategories) {
      if (numOfResultsOtherCategories[currentCategory]) {
        // filter out proposals from other categories
        updatedParams.numOfResultsOtherCategoriesTotal = numOfResults - numOfResultsOtherCategories[currentCategory];
        updatedParams.numOfResults = numOfResultsOtherCategories[currentCategory];
      } else {
        // no results in selected category?
        updatedParams.numOfResultsOtherCategoriesTotal = numOfResults;
        updatedParams.numOfResults =
          numOfResults - Object.values(numOfResultsOtherCategories).reduce((acc, num) => acc + Number(num), 0);
        if (updatedParams.numOfResults === 0) {
          updatedParams.success = false;
        }
      }
    }

    bi.logger().report(
      searchAndFiltersFinished({
        ...currentRequest,
        ...params,
        ...(currentRequest.filterCategory
          ? {
              numOfResults,
              numOfResultsOtherCategories: JSON.stringify(
                Object.fromEntries(
                  Object.entries(numOfResultsOtherCategories || {}).filter(([k]) => k !== currentCategory),
                ),
              ),
              ...updatedParams,
            }
          : { numOfResultsOtherCategories: JSON.stringify(numOfResultsOtherCategories) }),

        ...(errorType && errorType === ErrorTypes.CanceledByUser
          ? {} // do not add `loadingTime` param if request was cancelled
          : { loadingTime: Math.round(now() - requestStartTime) }),
      }),
    );

    currentRequest = null;
    requestStartTime = null;
  }, []);

  const { setNewSearchId } = useSearchResultsBI();

  return useMemo(
    () => ({
      trackSearchAndFiltersClear: (clickOrigin: ClickOriginsSearchAndFiltersClear) =>
        bi.logger().report(searchAndFiltersNoResultsTryToClick({ clickOrigin })),
      trackSearchAndFiltersStarted: (params: searchAndFiltersStartedParams = {}) => {
        if (currentRequest) {
          trackSearchAndFiltersFinished({
            ...currentRequest,
            ...{ success: false, errorType: ErrorTypes.CanceledByUser },
          });
        }
        currentRequest = { ...params, search_id: setNewSearchId(), pageName: BIPageName.MediaManager };
        requestStartTime = now();
        bi.logger().report(searchAndFiltersStarted(currentRequest));
      },
      trackSearchAndFiltersShowAllClick: (params: searchAndFiltersShowAllClickParams = {}) =>
        bi.logger().report(searchAndFiltersShowAllClick(params)),
      trackSearchAndFiltersFilterClick: (params: searchAndFiltersFilterClickParams) =>
        bi.logger().report(searchAndFiltersFilterClick(params)),
      trackSearchAndFiltersFinished,
      trackSearchAndFiltersSubscribeToLoadFinish: (itemsLoadPromise, pathname: string) => {
        itemsLoadPromise
          .then((items) => {
            const hasOtherCategories = items.some((item) => item.categoryId);
            const grouped = hasOtherCategories
              ? items.reduce((acc, item) => {
                  acc[item.categoryId] = (acc[item.categoryId] || 0) + 1;
                  return acc;
                }, {})
              : {};
            const params = items.length
              ? { success: true, numOfResults: items.length, numOfResultsOtherCategories: grouped }
              : {
                  success: false,
                  numOfResults: 0,
                  errorType: ErrorTypes.NoResults,
                };

            trackSearchAndFiltersFinished(params);
          })
          .catch(() => {
            trackSearchAndFiltersFinished({
              success: false,
              errorType: ErrorTypes.Technical,
            });
          });
      },
    }),
    [trackSearchAndFiltersFinished, setNewSearchId],
  );
};
